// Pure logic for the Route Planning screen: the API route <-> the editable list
// of stops, "has it changed?", and route-history wording. No React, no I/O.
import { formatDate } from "../../utils/tspFormat";
import { VISIT_STATE_TO_ROUTE_STATUS } from "../../constants/tspJourneyStatus";
import { placeFromRouteRow, placeLabel, placeToRouteItem, samePlace, tidyName } from "../../utils/tspPlace";

// The route is edited locally and only sent when saved. A stop is either a city
// already on the route (has `locationId`) or a newly added one, which carries the
// place picked in the State -> District -> City picker (see utils/tspPlace.js).

export function routeToStops(route) {
  if (!route) return [];
  return route.cities.map((city) => ({
    key: `city-${city.id}`,
    locationId: city.location_id,
    ...placeFromRouteRow(city),
    status: VISIT_STATE_TO_ROUTE_STATUS[city.visit_state],
    // A city that already has recorded visits cannot be removed (the server refuses too).
    hasVisits: city.visit_count > 0,
    // Saved stop details, shown only: a stop on the route is sent back as its
    // location_id, and the server keeps its details.
    latitude: typeof city.latitude === "number" ? city.latitude : null,
    longitude: typeof city.longitude === "number" ? city.longitude : null,
    areas: Array.isArray(city.areas) ? city.areas : [],
  }));
}

// Returns { ok, stops } or { ok: false, message }. `place` is the picker's place.
export function addStop(stops, place, keyFactory) {
  const city = tidyName(place?.city);
  if (!place || !place.stateId || !place.districtId || !city) return { ok: false, message: "Select a state, a district and a city first." };
  const candidate = { ...place, city, key: keyFactory(), status: "pending", hasVisits: false };
  const duplicate = stops.find((stop) => samePlace(stop, candidate));
  if (duplicate) return { ok: false, message: `${placeLabel(duplicate)} is already on the route.` };
  return { ok: true, stops: [...stops, candidate] };
}

export function moveStop(stops, index, direction) {
  const target = index + direction;
  if (target < 0 || target >= stops.length) return stops;
  const next = [...stops];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function removeStop(stops, key) {
  return stops.filter((stop) => stop.key !== key || stop.hasVisits);
}

// The whole desired route as the "save route" request body.
export function stopsToPayload(stops) {
  return stops.map((stop) => (stop.locationId ? { location_id: stop.locationId } : placeToRouteItem(stop)));
}

// Has the local route drifted from the saved one? (Order and membership.)
export function isRouteDirty(stops, route) {
  const saved = routeToStops(route);
  return stops.length !== saved.length || stops.some((stop, index) => stop.key !== saved[index].key);
}

// One route-history row -> a sentence. Only what the record holds: what changed,
// which city, and where in the route.
export function describeRouteChange(entry) {
  const name = entry.location_label;
  if (entry.action === "CITY_ADDED") return `Added ${name} at stop ${entry.new_sequence}`;
  if (entry.action === "CITY_REMOVED") return `Removed ${name} (was stop ${entry.previous_sequence})`;
  return `Moved ${name} from stop ${entry.previous_sequence} to stop ${entry.new_sequence}`;
}

export function toHistoryItem(entry) {
  return {
    id: entry.id,
    summary: describeRouteChange(entry),
    updatedBy: entry.changed_by?.name || "Unknown",
    updatedAt: formatDate(entry.changed_at.slice(0, 10)),
  };
}
